import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import RunAgentModal from '../components/RunAgentModal';
import OperatorChatModal from '../components/OperatorChatModal';
import DeploymentModal from '../components/DeploymentModal';
import { AgentStatusIndicator, deriveOperationalStatus } from '../components/AgentStatusIndicator';
import { WorkspaceHeader, WorkspaceContent, EmptyState } from '../components/WorkspaceLayout';
import { useToast } from '../hooks/use-toast';

import {
  ArrowSquareOut,
  Bell,
  BellSlash,
  Brain,
  Calendar,
  ChatCircleText,
  CircleNotch,
  Clock,
  ClockCounterClockwise,
  Copy,
  DotsThreeVertical,
  Envelope,
  FileText,
  FileXls,
  Image,
  Lightning,
  MagnifyingGlass,
  Microphone,
  MusicNote,
  Pause,
  PencilSimple,
  Play,
  PlayCircle,
  PlugsConnected,
  Plus,
  Robot,
  ShareNetwork,
  Sparkle,
  Square,
  SquaresFour,
  Trash,
  CheckCircle,
  Timer,
  Pulse
} from '@phosphor-icons/react';
import { AgentAvatar } from '../components/AgentAvatar';
import { OperationalMetrics } from '../components/OperationalMetrics';
// ════════════════════════════════════════════════════════════════════════════
// INPUT TYPE DISPLAY HELPERS
// ════════════════════════════════════════════════════════════════════════════

const INPUT_TYPE_CONFIG = {
  text: { label: 'Text', icon: ChatCircleText, color: 'blue' },
  audio: { label: 'Audio', icon: MusicNote, color: 'purple' },
  image: { label: 'Image', icon: Image, color: 'pink' },
  pdf: { label: 'PDF', icon: FileText, color: 'red' },
  docx: { label: 'Document', icon: FileText, color: 'blue' },
  xlsx: { label: 'Spreadsheet', icon: FileXls, color: 'green' },
  csv: { label: 'CSV', icon: FileXls, color: 'green' },
  json: { label: 'JSON', icon: FileText, color: 'yellow' },
  url: { label: 'URL', icon: ArrowSquareOut, color: 'cyan' },
  webhook_event: { label: 'Webhook', icon: PlugsConnected, color: 'orange' },
  calendar_event: { label: 'Calendar', icon: Calendar, color: 'teal' },
  email_thread: { label: 'Email', icon: Envelope, color: 'indigo' },
};

const COLOR_MAP = {
  blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  pink: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
  red: 'bg-red-500/10 text-red-400 border-red-500/20',
  green: 'bg-green-500/10 text-green-400 border-green-500/20',
  yellow: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  orange: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  cyan: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  teal: 'bg-teal-500/10 text-teal-400 border-teal-500/20',
  indigo: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
  gray: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
};

const getInputTypeChips = (inputTypes, maxDisplay = 2) => {
  if (!inputTypes || inputTypes.length === 0) inputTypes = ['text'];
  const chips = inputTypes.slice(0, maxDisplay).map(type => {
    const config = INPUT_TYPE_CONFIG[type] || { label: type, icon: FileText, color: 'gray' };
    return { type, ...config };
  });
  return { chips, overflow: Math.max(0, inputTypes.length - maxDisplay), allTypes: inputTypes };
};

const getCompletionModalPref = (agentId) => {
  try { return JSON.parse(localStorage.getItem('agentCompletionModalPrefs') || '{}')[agentId] !== false; }
  catch { return true; }
};
const saveCompletionModalPref = (agentId, showModal) => {
  try {
    const prefs = JSON.parse(localStorage.getItem('agentCompletionModalPrefs') || '{}');
    prefs[agentId] = showModal;
    localStorage.setItem('agentCompletionModalPrefs', JSON.stringify(prefs));
  } catch {}
};

const isVoiceAgent = (agent) => {
  const voiceProfiles = ['live_voice_s2s_agent', 'voice_input_agent', 'voice_output_tts_agent'];
  return voiceProfiles.includes(agent?.capability_profile) || agent?.voice_mode === 'live_s2s' || agent?.is_voice_template === true;
};

const getEditUrl = (agent) => {
  const agentId = agent?.id || agent?.agent_id;
  if (agent?.isOperator) return `/partner-pro/ai-workforce/operators/${agentId}`;
  if (isVoiceAgent(agent)) {
    if (agent?.capability_profile === 'voice_input_agent' && (agent?.requires_live_meeting_integration || agent?.wizard_type === 'meeting_integration'))
      return `/partner-pro/ai-workforce/agents/${agentId}/meeting-wizard`;
    return `/partner-pro/ai-workforce/agents/${agentId}/wizard`;
  }
  return `/partner-pro/ai-workforce/agents/${agentId}/edit`;
};

// ════════════════════════════════════════════════════════════════════════════
// STATUS & TYPE HELPERS
// ════════════════════════════════════════════════════════════════════════════

const STATUS_STYLES = {
  active: { dot: 'bg-green-400', badge: 'bg-green-500/15 text-green-400 border-green-500/25' },
  draft: { dot: 'bg-yellow-400', badge: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/25' },
  paused: { dot: 'bg-gray-400', badge: 'bg-gray-500/15 text-gray-400 border-gray-500/25' },
};

const getEffectiveStatus = (agent) => {
  if (agent.isOperator) return agent.hosting_status === 'active' ? 'active' : agent.status;
  if (agent.billing_status === 'active') return 'active';
  if (agent.is_active === true && agent.status !== 'active') return 'active';
  if (agent.status === 'active' && !agent.billing_status) return 'active';
  return agent.status;
};

const getAgentTypeInfo = (agent) => {
  if (agent.isOperator) return { label: 'Operator', icon: null, img: '/orion-icon-32.png' };
  const isVoice = agent.capability_profile === 'live_voice_s2s_agent' || agent.capability_profile === 'voice_output_tts_agent' || agent.capability_profile === 'voice_input_agent' || agent.interaction_mode === 'voice_realtime' || agent.voice_config?.enabled;
  if (isVoice) return { label: 'Voice', icon: Microphone };
  return { label: 'Text', icon: ChatCircleText };
};

// ════════════════════════════════════════════════════════════════════════════
// SKELETON CARD
// ════════════════════════════════════════════════════════════════════════════

const SkeletonCard = () => (
  <div className="rounded-[10px] border border-[var(--border-subtle)] bg-[var(--surface-1)] p-5 animate-pulse">
    <div className="flex items-start justify-between mb-3">
      <div className="w-11 h-11 rounded-lg bg-[rgba(255,255,255,0.06)]" />
      <div className="w-6 h-6 rounded bg-[rgba(255,255,255,0.04)]" />
    </div>
    <div className="h-4 w-3/5 bg-[rgba(255,255,255,0.06)] rounded mb-2" />
    <div className="h-5 w-16 bg-[rgba(255,255,255,0.04)] rounded mb-3" />
    <div className="h-3 w-full bg-[rgba(255,255,255,0.04)] rounded mb-1.5" />
    <div className="h-3 w-4/5 bg-[rgba(255,255,255,0.03)] rounded mb-3" />
    <div className="h-3 w-2/5 bg-[rgba(255,255,255,0.03)] rounded mb-3" />
    <div className="flex gap-2 mb-4"><div className="h-5 w-14 bg-[rgba(255,255,255,0.04)] rounded" /><div className="h-5 w-14 bg-[rgba(255,255,255,0.04)] rounded" /></div>
    <div className="border-t border-[rgba(255,255,255,0.05)] pt-3"><div className="h-9 w-full bg-[rgba(255,255,255,0.04)] rounded-lg" /></div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// AGENT CARD
// ════════════════════════════════════════════════════════════════════════════

const AgentCard = ({ agent, onRun, onOperatorChat, onEdit, onNavigate, onDelete, onDuplicate, onToggleStatus, onStartOperator, onPauseOperator, onStopOperator, onDeploy, processingId, formatDate }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { toast } = useToast();
  const agentId = agent.id || agent.agent_id;
  const effectiveStatus = getEffectiveStatus(agent);
  const statusStyle = STATUS_STYLES[effectiveStatus] || STATUS_STYLES.draft;
  const typeInfo = getAgentTypeInfo(agent);
  const { chips } = getInputTypeChips(agent.input_types, 3);

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="group relative rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-2)] hover:border-[var(--border-strong)] hover:shadow-[var(--shadow-md)] transition-all duration-200 flex flex-col"
      data-testid={`agent-card-${agentId}`}
    >
      {/* Card body - clickable to navigate */}
      <div className="p-5 flex-1 cursor-pointer" onClick={() => onNavigate(agent)}>
        {/* Top row: avatar + kebab menu */}
        <div className="flex items-start justify-between mb-3">
          <AgentAvatar name={agent.name} size="md" agentType={agent.agent_type || agent.agentType || agent.capability_profile} iconUrl={agent.icon_url} agent={agent} />
          {/* Kebab menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
              className="p-1.5 rounded-md hover:bg-[rgba(255,255,255,0.06)] transition-colors text-[#555] hover:text-white"
              data-testid={`agent-menu-${agentId}`}
            >
              <DotsThreeVertical size={16} weight="bold" />
            </button>
            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.1 }}
                  className="absolute right-0 top-8 z-50 w-52 bg-[var(--surface-2)] border border-[var(--border-default)] rounded-[10px] shadow-[var(--shadow-xl)] py-1.5 overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MenuItem icon={ArrowSquareOut} label="View Details" color="text-[#818CF8]" onClick={() => { onNavigate(agent); setMenuOpen(false); }} />
                  {!agent.isOperator && effectiveStatus === 'active' && (
                    <MenuItem icon={PlayCircle} label="Run Worker" color="text-green-400" onClick={() => { onRun(agent); setMenuOpen(false); }} />
                  )}
                  {!agent.isOperator && effectiveStatus === 'active' && (
                    <MenuItem icon={ChatCircleText} label="Chat with Agent" color="text-[#818CF8]" onClick={() => { window.location.href = `/unified-agent-builder/${agentId}?tab=chat`; }} />
                  )}
                  {agent.isOperator && ['draft', 'configured', 'stopped', 'paused'].includes(agent.status) && (
                    <MenuItem icon={Play} label="Start Operator" color="text-green-400" onClick={() => { onStartOperator(agent); setMenuOpen(false); }} disabled={processingId === agentId} />
                  )}
                  {agent.isOperator && agent.status === 'active' && (
                    <MenuItem icon={Pause} label="Pause Operator" color="text-yellow-400" onClick={() => { onPauseOperator(agent); setMenuOpen(false); }} disabled={processingId === agentId} />
                  )}
                  {agent.isOperator && ['active', 'paused'].includes(agent.status) && (
                    <MenuItem icon={Square} label="Stop Operator" color="text-red-400" onClick={() => { onStopOperator(agent); setMenuOpen(false); }} disabled={processingId === agentId} />
                  )}
                  {agent.isOperator && agent.status === 'active' && (
                    <MenuItem icon={ChatCircleText} label="Chat with Operator" color="text-pink-400" onClick={() => { onOperatorChat(agent); setMenuOpen(false); }} />
                  )}

                  <Divider />
                  {!agent.isOperator && <MenuItem icon={ClockCounterClockwise} label="View Sessions" onClick={() => { window.location.href = `/partner-pro/ai-workforce/sessions?agent_id=${agentId}`; }} />}
                  {!agent.isOperator && <MenuItem icon={SquaresFour} label="Worker Dashboard" onClick={() => { window.location.href = `/agents/${agentId}/dashboard`; }} />}
                  <MenuItem icon={PencilSimple} label={agent.isOperator ? 'Configure Operator' : 'Edit Agent'} onClick={() => { onEdit(agent); setMenuOpen(false); }} />
                  {!agent.isOperator && (effectiveStatus === 'active') && (
                    <MenuItem icon={ShareNetwork} label="Deploy / Share" onClick={() => { onDeploy(agent); setMenuOpen(false); }} />
                  )}
                  {!agent.isOperator && <MenuItem icon={Copy} label="Duplicate" onClick={() => { onDuplicate(agent); setMenuOpen(false); }} />}
                  {!agent.isOperator && effectiveStatus === 'active' && (
                    <MenuItem
                      icon={getCompletionModalPref(agentId) ? BellSlash : Bell}
                      label={getCompletionModalPref(agentId) ? 'Disable Completion Modal' : 'Enable Completion Modal'}
                      onClick={() => {
                        const cur = getCompletionModalPref(agentId);
                        saveCompletionModalPref(agentId, !cur);
                        toast({ title: !cur ? 'Completion modal enabled' : 'Completion modal disabled' });
                        setMenuOpen(false);
                      }}
                    />
                  )}
                  {!agent.isOperator && (
                    <MenuItem icon={effectiveStatus === 'active' ? Pause : Play} label={effectiveStatus === 'active' ? 'Pause Worker' : 'Activate Worker'}
                      onClick={() => { onToggleStatus(agentId, agent.status); setMenuOpen(false); }} />
                  )}
                  <Divider />
                  <MenuItem icon={Trash} label="Delete" color="text-red-400" hoverBg="hover:bg-red-500/10" onClick={() => { onDelete(agent); setMenuOpen(false); }} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Agent title — own row */}
        <h3 className="text-sm font-semibold text-white leading-snug mb-1" data-testid="agent-card-name">{agent.name}</h3>

        {/* Status badge + type — own row */}
        <div className="flex items-center gap-2 mb-2.5">
          <AgentStatusIndicator status={effectiveStatus} variant="badge" size="xs" />
          <AgentStatusIndicator status={deriveOperationalStatus(agent)} variant="dot" size="xs" />
          <span className="text-[10px] text-[#555] font-medium">{typeInfo.label}</span>
        </div>

        {/* Description — 2 line max */}
        {agent.description && (
          <p className="text-xs text-[#6b7080] line-clamp-2 mb-3 leading-relaxed" data-testid="agent-card-description">{agent.description}</p>
        )}

        {/* Operational metrics */}
        <OperationalMetrics
          lastRunAt={agent.last_run_at}
          runsToday={agent.runs_today || 0}
          successRate={agent.success_rate != null ? agent.success_rate : null}
          className="mb-3"
        />

        {/* Capability chips */}
        {!agent.isOperator && chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {chips.map((chip, i) => {
              const IconC = chip.icon;
              return (
                <span key={i} className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded border ${COLOR_MAP[chip.color] || COLOR_MAP.gray}`}>
                  <IconC size={9} weight="duotone" /> {chip.label}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Divider + Action button */}
      <div className="px-5 pb-4 pt-0">
        <div className="border-t border-[rgba(255,255,255,0.05)] pt-3"></div>
        {!agent.isOperator && effectiveStatus === 'active' && (
          <button
            onClick={(e) => { e.stopPropagation(); onRun(agent); }}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#14F195]/10 border border-[#14F195]/20 text-[#14F195] text-xs font-semibold hover:bg-[#14F195]/20 transition-all"
            data-testid={`run-agent-${agentId}`}
          >
            <PlayCircle size={14} weight="duotone" /> Run Worker
          </button>
        )}
        {!agent.isOperator && effectiveStatus !== 'active' && (
          <button
            onClick={(e) => { e.stopPropagation(); onNavigate(agent); }}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-[#8b8fa3] text-xs font-medium hover:bg-[rgba(255,255,255,0.07)] transition-colors"
            data-testid={`activate-agent-${agentId}`}
          >
            <Play size={14} weight="duotone" /> Activate Worker
          </button>
        )}
        {agent.isOperator && agent.status === 'active' && (
          <button
            onClick={(e) => { e.stopPropagation(); onOperatorChat(agent); }}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-pink-500/10 border border-pink-500/20 text-pink-400 text-xs font-semibold hover:bg-pink-500/20 transition-all"
            data-testid={`chat-operator-${agentId}`}
          >
            <ChatCircleText size={14} weight="duotone" /> Chat with Operator
          </button>
        )}
        {agent.isOperator && agent.status !== 'active' && (
          <button
            onClick={(e) => { e.stopPropagation(); onStartOperator(agent); }}
            disabled={processingId === agentId}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-semibold hover:bg-green-500/20 transition-all disabled:opacity-50"
            data-testid={`start-operator-${agentId}`}
          >
            {processingId === agentId ? <CircleNotch size={14} className="animate-spin" /> : <Play size={14} weight="duotone" />} Start Operator
          </button>
        )}
      </div>
    </motion.div>
  );
};

const MenuItem = ({ icon: Icon, label, color = 'text-[#c0c4d0]', hoverBg = 'hover:bg-[rgba(255,255,255,0.05)]', onClick, disabled }) => (
  <button onClick={onClick} disabled={disabled} className={`w-full px-3 py-2 text-left text-xs ${color} ${hoverBg} flex items-center gap-2.5 transition-colors disabled:opacity-40`}>
    <Icon size={13} weight="duotone" /> {label}
  </button>
);

const Divider = () => <div className="border-t border-[rgba(255,255,255,0.08)] my-1" />;

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const AgentList = () => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { toast } = useToast();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgentForRun, setSelectedAgentForRun] = useState(null);
  const [selectedOperatorForChat, setSelectedOperatorForChat] = useState(null);
  const [deployAgent, setDeployAgent] = useState(null);

  useEffect(() => { if (activeOrganization) fetchAgents(); }, [activeOrganization, statusFilter]);

  const fetchAgents = async () => {
    if (!activeOrganization) return;
    try {
      setLoading(true);
      // Use unified Worker API
      const statusParam = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await fetchWithOrg(`${BACKEND_URL}/api/workers${statusParam}`, {}, activeOrganization?.organization_id);
      let allAgents = [];
      if (res.ok) {
        const data = await res.json();
        allAgents = (data.workers || []).map(w => ({
          ...w,
          agent_id: w.id,
          description: w.configuration?.description || '',
          short_description: w.configuration?.short_description || '',
          is_active: w.status === 'active',
          agentType: w.type === 'operator' ? 'operator' : w.type === 'voice' ? 'voice' : 'text',
          isOperator: w.type === 'operator',
          accepted_inputs: w.capabilities?.length > 0 ? w.capabilities : ['text'],
          // Keep capability_profile as string for AgentAvatar and voice detection compatibility
          capability_profile: w.configuration?.capability_profile || w.type || '',
          total_runs: w.run_count || 0,
          organization_id: w.metadata?.org_id || activeOrganization.organization_id,
        }));
      }
      allAgents.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setAgents(allAgents);
    } catch (error) {
      console.error('Error fetching workers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (agentId, currentStatus) => {
    if (currentStatus === 'draft' || currentStatus === 'paused') { navigate(`/partner-pro/ai-workforce/agents/${agentId}`); return; }
    setProcessingId(agentId);
    try {
      const response = await fetchWithOrg(`${BACKEND_URL}/api/workers/${agentId}/status`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'paused' }) }, activeOrganization?.organization_id);
      if (response.ok) { toast({ title: "Worker Paused", description: "The worker has been paused." }); fetchAgents(); }
    } catch (error) { toast({ title: "Error", description: "Failed to pause worker.", variant: "destructive" }); } finally { setProcessingId(null); }
  };

  const handleStartOperator = async (operator) => {
    setProcessingId(operator.id);
    try {
      const response = await fetchWithOrg(`${BACKEND_URL}/api/exec/agents/${operator.id}/start`, { method: 'POST' }, activeOrganization.organization_id);
      if (response.ok) { toast({ title: "Operator Started", description: `"${operator.name}" is now running.` }); fetchAgents(); }
      else { const err = await response.json(); toast({ title: "Start Failed", description: err.detail?.message || err.detail || "Unable to start operator", variant: "destructive" }); }
    } catch { toast({ title: "Error", description: "Failed to start operator.", variant: "destructive" }); } finally { setProcessingId(null); }
  };

  const handlePauseOperator = async (operator) => {
    setProcessingId(operator.id);
    try {
      const response = await fetchWithOrg(`${BACKEND_URL}/api/exec/agents/${operator.id}/pause`, { method: 'POST' }, activeOrganization.organization_id);
      if (response.ok) { toast({ title: "Operator Paused", description: `"${operator.name}" has been paused.` }); fetchAgents(); }
      else { const err = await response.json(); toast({ title: "Pause Failed", description: err.detail?.message || err.detail || "Unable to pause", variant: "destructive" }); }
    } catch { toast({ title: "Error", description: "Failed to pause operator.", variant: "destructive" }); } finally { setProcessingId(null); }
  };

  const handleStopOperator = async (operator) => {
    setProcessingId(operator.id);
    try {
      const response = await fetchWithOrg(`${BACKEND_URL}/api/exec/agents/${operator.id}/stop`, { method: 'POST' }, activeOrganization.organization_id);
      if (response.ok) { toast({ title: "Operator Stopped", description: `"${operator.name}" has been stopped.` }); fetchAgents(); }
      else { const err = await response.json(); toast({ title: "Stop Failed", description: err.detail?.message || err.detail || "Unable to stop", variant: "destructive" }); }
    } catch { toast({ title: "Error", description: "Failed to stop operator.", variant: "destructive" }); } finally { setProcessingId(null); }
  };

  const handleDelete = async (agent) => {
    const itemType = agent.isOperator ? 'operator' : 'agent';
    const isSubscribed = agent.status === 'active' || agent.status === 'paused';
    const hasSubscription = agent.stripe_subscription_id || agent.billing_enabled;
    let msg;
    if (isSubscribed && hasSubscription) {
      msg = `This ${itemType} has an active subscription.\n\nDeleting will:\n- Cancel your subscription immediately\n- Remove the ${itemType} from your list\n\nYou can reactivate from Billing within the current period.\n\nContinue?`;
    } else {
      msg = `Are you sure you want to delete this ${itemType}?\n\nThis action cannot be undone.`;
    }
    if (!confirm(msg)) return;
    setProcessingId(agent.id);
    try {
      const endpoint = agent.isOperator ? `${BACKEND_URL}/api/exec/agents/${agent.id}` : `${BACKEND_URL}/api/agents/${agent.id}`;
      const response = await fetchWithOrg(endpoint, { method: 'DELETE' }, activeOrganization.organization_id);
      if (response.ok) {
        const result = await response.json();
        toast({ title: result.soft_delete ? "Worker Archived" : "Deleted", description: result.soft_delete ? "Subscription cancelled. Reactivate from Billing." : `The ${itemType} has been permanently deleted.` });
        fetchAgents();
      } else { const err = await response.json(); toast({ title: "Delete Failed", description: err.detail?.message || err.detail || `Unable to delete ${itemType}`, variant: "destructive" }); }
    } catch { toast({ title: "Error", description: `Failed to delete ${itemType}.`, variant: "destructive" }); } finally { setProcessingId(null); }
  };

  const handleDuplicate = async (agent) => {
    setProcessingId(agent.id);
    try {
      const response = await fetchWithOrg(`${BACKEND_URL}/api/agents`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: `${agent.name} (Copy)`, role: agent.role, template_id: agent.template_id, installation_config: agent.installation_config, status: 'draft' })
      }, activeOrganization.organization_id);
      if (response.ok) { toast({ title: "Worker Duplicated" }); fetchAgents(); }
    } catch {} finally { setProcessingId(null); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleNavigate = (agent) => {
    if (agent.isOperator) navigate(`/partner-pro/ai-workforce/operators/${agent.id}`);
    else navigate(`/partner-pro/ai-workforce/agents/${agent.id}`);
  };

  // Filter agents
  const filteredAgents = agents.filter(agent => {
    if (typeFilter === 'voice' && !isVoiceAgent(agent) && !agent.isOperator) return false;
    if (typeFilter === 'text' && (isVoiceAgent(agent) || agent.isOperator)) return false;
    if (typeFilter === 'operator' && !agent.isOperator) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return agent.name?.toLowerCase().includes(q) || agent.description?.toLowerCase().includes(q) || agent.department?.toLowerCase().includes(q);
    }
    return true;
  });

  const counts = { all: agents.length, active: agents.filter(a => getEffectiveStatus(a) === 'active').length };

  return (
    <div className="min-h-screen bg-[var(--surface-0)] pb-8" data-testid="agent-list-page">
      <WorkspaceHeader
        icon={Robot}
        title="AI Workforce"
        description={counts.all > 0 ? `${counts.active} active of ${counts.all} workers` : 'Manage and run your workers'}
        actions={
          <>
            <button onClick={() => navigate('/automations')} data-testid="browse-automations-btn"
              className="flex items-center gap-2 px-3.5 py-2 bg-[var(--surface-3)] hover:bg-[var(--surface-4)] border border-[var(--border-default)] text-white text-xs font-medium rounded-md transition-colors">
              <Sparkle size={14} weight="duotone" /> Browse Workers
            </button>
            <button onClick={() => navigate('/unified-agent-builder')} data-testid="create-agent-btn"
              className="flex items-center gap-2 px-3.5 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-xs font-medium rounded-md transition-colors">
              <Plus size={14} weight="bold" /> Create Worker
            </button>
          </>
        }
      >
        {/* Search + Filters */}
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input type="text" placeholder="Search workers..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="agent-search-input"
              className="w-full pl-9 pr-4 py-2 bg-[var(--surface-2)] border border-[var(--border-default)] rounded-md text-white text-xs placeholder-[var(--text-muted)] focus:outline-none focus:border-[#6366F1]/40 transition-colors" />
          </div>
          <div className="flex items-center gap-1">
            {['all', 'active', 'draft', 'paused'].map(f => (
              <button key={f} onClick={() => setStatusFilter(f)} data-testid={`status-filter-${f}`}
                className={`px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-all ${statusFilter === f ? 'bg-[#6366F1] text-white' : 'bg-[var(--surface-2)] text-[var(--text-muted)] hover:text-white hover:bg-[var(--surface-3)]'}`}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            {[
              { value: 'all', label: 'All Types', icon: null },
              { value: 'text', label: 'Text', icon: ChatCircleText },
              { value: 'voice', label: 'Voice', icon: Microphone },
              { value: 'operator', label: 'Operator', img: '/orion-icon-32.png' }
            ].map(f => (
              <button key={f.value} onClick={() => setTypeFilter(f.value)} data-testid={`type-filter-${f.value}`}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-all ${typeFilter === f.value ? 'bg-[#8B5CF6] text-white' : 'bg-[var(--surface-2)] text-[var(--text-muted)] hover:text-white hover:bg-[var(--surface-3)]'}`}>
                {f.img ? <img src={f.img} alt="" className="w-3 h-3 rounded-sm" /> : f.icon ? <f.icon size={11} weight="duotone" /> : null}
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </WorkspaceHeader>

      <WorkspaceContent>
        {/* AI Workforce Summary Row */}
        {!loading && filteredAgents.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="workforce-summary-row">
            {(() => {
              const activeCount = filteredAgents.filter(a => getEffectiveStatus(a) === 'active').length;
              const totalRunsToday = filteredAgents.reduce((sum, a) => sum + (a.runs_today || 0), 0);
              const agentsWithRate = filteredAgents.filter(a => a.success_rate != null);
              const avgSuccess = agentsWithRate.length > 0 ? Math.round(agentsWithRate.reduce((s, a) => s + a.success_rate, 0) / agentsWithRate.length) : null;
              const agentsWithRuntime = filteredAgents.filter(a => a.avg_runtime_ms > 0);
              const avgRuntime = agentsWithRuntime.length > 0 ? Math.round(agentsWithRuntime.reduce((s, a) => s + a.avg_runtime_ms, 0) / agentsWithRuntime.length / 1000) : null;

              const stats = [
                { label: 'Active Workers', value: activeCount, icon: Pulse, color: 'text-emerald-400' },
                { label: 'Runs Today', value: totalRunsToday, icon: Lightning, color: 'text-amber-400' },
                { label: 'Success Rate', value: avgSuccess !== null ? `${avgSuccess}%` : '—', icon: CheckCircle, color: 'text-[#6366F1]' },
                { label: 'Avg Runtime', value: avgRuntime !== null ? `${avgRuntime}s` : '—', icon: Timer, color: 'text-cyan-400' },
              ];
              return stats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div key={stat.label} className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[var(--surface-1)] border border-[var(--border-subtle)]" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <Icon size={16} weight="duotone" className={`${stat.color} shrink-0`} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white leading-tight">{stat.value}</p>
                      <p className="text-[10px] text-[#555] leading-tight">{stat.label}</p>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="agent-loading-skeletons">
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : filteredAgents.length === 0 ? (
          <EmptyState
            icon={Brain}
            title={searchQuery ? 'No matching workers' : typeFilter !== 'all' ? `No ${typeFilter} workers` : statusFilter !== 'all' ? `No ${statusFilter} workers` : 'Build Your AI Workforce'}
            description={searchQuery ? `No workers match "${searchQuery}". Try a different search.`
              : statusFilter !== 'all' || typeFilter !== 'all' ? 'Try adjusting your filters to see more workers.'
              : 'Install workers to analyse, create, and automate parts of your business.'}
            action={
              statusFilter === 'all' && typeFilter === 'all' && !searchQuery ? (
                <button onClick={() => navigate('/unified-agent-builder')}
                  className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-medium rounded-md transition-colors">
                  <Plus size={14} weight="bold" /> Create Worker
                </button>
              ) : null
            }
            secondaryAction={
              (searchQuery || statusFilter !== 'all' || typeFilter !== 'all') ? (
                <button onClick={() => { setSearchQuery(''); setStatusFilter('all'); setTypeFilter('all'); }} data-testid="clear-filters-btn"
                  className="px-4 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] text-white text-sm font-medium rounded-md hover:bg-[var(--surface-4)] transition-colors">
                  Clear Filters
                </button>
              ) : statusFilter === 'all' && typeFilter === 'all' && !searchQuery ? (
                <button onClick={() => navigate('/automations')} data-testid="browse-automations-empty-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] text-white text-sm font-medium rounded-md hover:bg-[var(--surface-4)] transition-colors">
                  <Sparkle size={14} weight="duotone" /> Browse Automations
                </button>
              ) : null
            }
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="agent-card-grid">
            {filteredAgents.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                onRun={setSelectedAgentForRun}
                onOperatorChat={setSelectedOperatorForChat}
                onEdit={(a) => navigate(getEditUrl(a))}
                onNavigate={handleNavigate}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
                onToggleStatus={handleToggleStatus}
                onStartOperator={handleStartOperator}
                onPauseOperator={handlePauseOperator}
                onStopOperator={handleStopOperator}
                onDeploy={setDeployAgent}
                processingId={processingId}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}
      </WorkspaceContent>

      {/* Run Agent Modal */}
      {selectedAgentForRun && (
        <RunAgentModal
          agent={selectedAgentForRun}
          isOpen={!!selectedAgentForRun}
          onClose={() => setSelectedAgentForRun(null)}
          onSuccess={(sessionId) => { console.log('Session completed:', sessionId); }}
        />
      )}

      {/* Operator Chat Modal */}
      <OperatorChatModal
        isOpen={!!selectedOperatorForChat}
        onClose={() => setSelectedOperatorForChat(null)}
        operator={selectedOperatorForChat}
      />

      {/* Deployment Modal */}
      {deployAgent && (
        <DeploymentModal
          agentId={deployAgent.id || deployAgent.agent_id}
          agentName={deployAgent.name}
          isOpen={!!deployAgent}
          onClose={() => setDeployAgent(null)}
        />
      )}
    </div>
  );
};

export default AgentList;
